// shared/src/types/runs.ts
// TypeScript surface for the runs + params_snapshots tables (migration 0022).
//
// Used by:
//   · workers/cron-dispatcher (writer)
//   · workers/api-worker      (reader, routes /api/runs/*)
//   · apps/web                (Runs tab, drilldown drawer)
//
// All string-literal enums kept in lock-step with the CHECK constraints in
// migrations/0022_runs_registry.sql.

import type { ScheduleTarget } from '../cron-schedule.js';

// ─── Enums (lock-stepped with 0022 CHECK constraints) ───────────────────────

export type RunScheduler = ScheduleTarget;
// 2026-06-04 (C6): +ACCOUNT (Account Holdings & Value, migration 0041/0042).
// 2026-06-19: +EXPORT (PAX durable export, migration 0063).
// 2026-07-14: +ORACLE (Data Oracle 5-min consensus price, migration 0084).
// 2026-08-09: +PURGE (nightly D1 retention purge split out of the CHAIN target so the
// brief's next_fires.CHAIN countdown stops conflating the purge with brief fires;
// migration 0125). Same chain-workflow RPC (runRetentionPurge), distinct scheduler.
// Lock-stepped with the runs.scheduler CHECK rebuilt in migration 0125.
export const RUN_SCHEDULERS: readonly RunScheduler[] =
  ['REFRESH','CHAIN','AMM','CLOSE','SCORER','MACRO_SCORER','BACKUP','ACCOUNT','EXPORT','ORACLE','PURGE'] as const;

export type RunStatus = 'running' | 'success' | 'failed' | 'terminated' | 'skipped';
export const RUN_STATUSES: readonly RunStatus[] =
  ['running','success','failed','terminated','skipped'] as const;

export type RunTrigger = 'cron' | 'manual' | 'reap';
export const RUN_TRIGGERS: readonly RunTrigger[] = ['cron','manual','reap'] as const;

// Q&A refreshes (R4 zone) are NOT pipeline runs, but the Ops-Lab Runs tab shows
// a trigger column for them mirroring R3. A Q&A refresh is either auto ('cron' —
// the chain-workflow q.1.qa fan-out on each new brief) or 'manual' (user clicks ↻
// on the /brief Analyst Q&A zone). 'reap' never applies to Q&A. Threaded through
// the runQaSingle RPC and persisted to llm_calls.source (migration 0034).
export type QaRunTrigger = 'cron' | 'manual';
export const QA_RUN_TRIGGERS: readonly QaRunTrigger[] = ['cron','manual'] as const;

// Terminal = no further state transitions. Used by the WS push layer to know
// when to stop sending updates for a given run_id.
export const TERMINAL_RUN_STATUSES: readonly RunStatus[] =
  ['success','failed','terminated','skipped'] as const;

export function isTerminalRunStatus(s: RunStatus): boolean {
  return TERMINAL_RUN_STATUSES.includes(s);
}

// ─── Row shapes ─────────────────────────────────────────────────────────────

export interface RunRow {
  id:               string;
  scheduler:        RunScheduler;
  method:           string;
  status:           RunStatus;
  scheduled_at:     string;
  started_at:       string | null;
  finished_at:      string | null;
  duration_ms:      number | null;
  trigger:          RunTrigger;
  triggered_by:     string | null;
  instance_id:      string | null;
  payload_summary:  string | null;   // JSON-stringified or null
  error_message:    string | null;
  parent_run_id:    string | null;
  cancel_requested: 0 | 1;
  params_hash:      string | null;
  created_at:       string;
  // Read-derived (NOT a runs-table column). The /api/runs list query LEFT JOINs
  // briefs.run_cost_usd via runs.instance_id = briefs.chain_run_id, so only CHAIN
  // runs carry a cost; every other scheduler (and any run without a linked brief)
  // is null → the R3 zone renders '—'. Defaulted to null by RunRowSchema for the
  // queries that don't join (/active, /:id, /:id/kill, /:id/rerun).
  cost_usd:         number | null;
  // Read-derived (NOT a runs-table column). Sibling of cost_usd: the /api/runs list query
  // reads briefs.run_shadow_cost_usd (per-brief shadow-A/B challenger spend, kept OUT of
  // run_cost_usd since 2026-07-11 migration 0081). NULL for non-CHAIN runs, pre-0081 briefs,
  // and briefs with no shadow. docs/shadow-cost-attribution-2026-07-11.
  shadow_cost_usd:  number | null;
}

export interface ParamSnapshotRow {
  hash:          string;
  snapshot_json: string;             // JSON of { [key]: { value, type } }
  created_at:    string;
}

// Decoded snapshot for UI display.
export interface ParamSnapshotEntry {
  key:   string;
  value: string;
  type:  string;
}
